import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_PT_BR } from "material-react-table/locales/pt-BR";
import { Button, Divider, Typography, Modal, Tag, Progress, Tooltip, message, Alert } from "antd";
import { Content } from "antd/es/layout/layout";
import { AiFillDelete, AiOutlineSearch } from "react-icons/ai";
import { FaCloudUploadAlt, FaPlay, FaCopy, FaSync } from "react-icons/fa";
import { request, token } from "../../../functions/request";
import Aguarde from "../../../components/aguarde/aguarde";
import HlsPlayer from "../../../components/Video/HlsPlayer";
import ModalBackupUpload from "./ModalBackupUpload";

const STATUS = {
  pendente: { cor: "default", texto: "Na fila" },
  processando: { cor: "processing", texto: "Transcodificando" },
  concluido: { cor: "success", texto: "Concluído" },
  erro: { cor: "error", texto: "Erro" },
};

// Intervalo do polling enquanto houver job rodando. O progresso vem do
// meta.json no servidor, que a fila atualiza no máximo 1x por segundo.
const INTERVALO_POLLING_MS = 4000;

function formatarBytes(bytes) {
  if (!bytes) return "-";
  const unidades = ["B", "KB", "MB", "GB", "TB"];
  const indice = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, indice)).toFixed(1)} ${unidades[indice]}`;
}

function formatarDuracao(segundos) {
  if (!segundos) return "-";
  const total = Math.round(segundos);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const doisDigitos = (n) => String(n).padStart(2, "0");
  return h > 0
    ? `${h}:${doisDigitos(m)}:${doisDigitos(s)}`
    : `${doisDigitos(m)}:${doisDigitos(s)}`;
}

function formatarData(iso) {
  if (!iso) return "-";
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? "-" : data.toLocaleString("pt-BR");
}

const VideosBackup = () => {
  const [backups, setBackups] = useState([]);
  const [fila, setFila] = useState({ naFila: 0, processando: false });
  const [carregou, setCarregou] = useState(false);
  const [erroCarga, setErroCarga] = useState(null);

  const [modalUploadAberto, setModalUploadAberto] = useState(false);
  const [preview, setPreview] = useState(null);
  const [paraExcluir, setParaExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);

  // Guarda o estado atual pro intervalo de polling não precisar ser recriado a
  // cada render (e não reiniciar o contador junto).
  const temJobAtivoRef = useRef(false);

  const carregar = useCallback(async () => {
    try {
      // fetch direto (e nao o helper request) porque aqui interessa o corpo da
      // resposta de erro: a API diz exatamente o que faltou configurar, e essa
      // mensagem vale muito mais que um "Internal Server Error" generico.
      const resposta = await fetch(process.env.REACT_APP_API_RB + "/video_backup/list", {
        headers: { "Content-Type": "application/json", "x-access-token": token },
      });
      const corpo = await resposta.json().catch(() => null);

      if (!resposta.ok) {
        throw new Error(corpo?.error || resposta.statusText || "Falha ao carregar backups");
      }

      const lista = corpo?.backups || [];
      setBackups(lista);
      setFila(corpo?.fila || { naFila: 0, processando: false });
      setErroCarga(null);
      temJobAtivoRef.current = lista.some(
        (b) => b.status === "processando" || b.status === "pendente"
      );
    } catch (error) {
      console.error("Erro ao carregar backups:", error);
      setErroCarga(`Não foi possível carregar os backups: ${error.message}`);
    } finally {
      setCarregou(true);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    const intervalo = setInterval(() => {
      if (temJobAtivoRef.current) carregar();
    }, INTERVALO_POLLING_MS);
    return () => clearInterval(intervalo);
  }, [carregar]);

  // Copia só o código do backup (id da pasta, ex.:
  // 1790022378540_18-09-analise-de-mercado-boi-rb-video), que é o valor que
  // vai na coluna vid_end do vídeo — e não a URL completa do master.m3u8.
  const copiarCodigo = async (codigo) => {
    try {
      await navigator.clipboard.writeText(codigo);
      message.success("Código copiado!");
    } catch (_) {
      message.error("Não foi possível copiar o código.");
    }
  };

  const excluir = async () => {
    if (!paraExcluir) return;
    setExcluindo(true);
    try {
      await request(`/video_backup/delete/${paraExcluir.id}`, { method: "DELETE" });
      message.success("Backup excluído.");
      setParaExcluir(null);
      carregar();
    } catch (error) {
      console.error(error);
      message.error("Erro ao excluir o backup.");
    } finally {
      setExcluindo(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "titulo",
        header: "Título",
        size: 200,
        Cell: ({ row }) => (
          <Content style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {row.original.urls?.thumbnail && row.original.status === "concluido" && (
              <img
                src={row.original.urls.thumbnail}
                alt={row.original.titulo}
                width={80}
                height={45}
                style={{ objectFit: "cover", borderRadius: 4, flexShrink: 0 }}
              />
            )}
            <Content>
              <Typography.Text strong>{row.original.titulo}</Typography.Text>
              <br />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.original.arquivoOriginal}
              </Typography.Text>
            </Content>
          </Content>
        ),
      },
      {
        accessorKey: "vidEnd",
        header: "Fallback (vid_end)",
        size: 140,
        Cell: ({ row }) => (
          <Content>
            <Typography.Text code>{row.original.vidEnd || "-"}</Typography.Text>
            {row.original.vidCdn && (
              <>
                <br />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  CDN: {row.original.vidCdn}
                </Typography.Text>
              </>
            )}
          </Content>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 160,
        Cell: ({ row }) => {
          const { status, progresso, etapa, erro } = row.original;
          const info = STATUS[status] || { cor: "default", texto: status };

          return (
            <Content>
              <Tag color={info.cor}>{info.texto}</Tag>
              {status === "processando" && (
                <Progress
                  percent={progresso || 0}
                  size="small"
                  status="active"
                  format={(p) => `${p}%${etapa ? ` · ${etapa}` : ""}`}
                />
              )}
              {status === "erro" && (
                <Tooltip title={erro}>
                  <Typography.Text type="danger" style={{ fontSize: 11 }} ellipsis>
                    {erro}
                  </Typography.Text>
                </Tooltip>
              )}
            </Content>
          );
        },
      },
      {
        accessorKey: "renditions",
        header: "Qualidades",
        size: 120,
        Cell: ({ row }) =>
          (row.original.renditions || []).length ? (
            <Content style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {row.original.renditions.map((r) => (
                <Tag key={r.nome} style={{ margin: 0 }}>
                  {r.nome}
                </Tag>
              ))}
            </Content>
          ) : (
            "-"
          ),
      },
      {
        accessorKey: "duracao",
        header: "Duração",
        size: 80,
        Cell: ({ cell }) => formatarDuracao(cell.getValue()),
      },
      {
        accessorKey: "bytesSaida",
        header: "Tamanho",
        size: 90,
        Cell: ({ cell }) => formatarBytes(cell.getValue()),
      },
      {
        accessorKey: "criadoEm",
        header: "Criado em",
        size: 140,
        Cell: ({ cell }) => formatarData(cell.getValue()),
      },
    ],
    []
  );

  return (
    <>
      <ModalBackupUpload
        open={modalUploadAberto}
        onCancel={() => setModalUploadAberto(false)}
        onFinish={carregar}
      />

      <Modal
        title={preview?.titulo}
        open={!!preview}
        onCancel={() => setPreview(null)}
        footer={null}
        width={860}
        destroyOnClose
      >
        {preview && (
          <>
            <HlsPlayer src={preview.urls?.master} poster={preview.urls?.thumbnail} />
            <Typography.Paragraph copyable={{ text: preview.urls?.master }} style={{ marginTop: 12 }}>
              <Typography.Text type="secondary">{preview.urls?.master}</Typography.Text>
            </Typography.Paragraph>
          </>
        )}
      </Modal>

      <Modal
        title="Você tem certeza que deseja excluir esse backup?"
        open={!!paraExcluir}
        onCancel={() => setParaExcluir(null)}
        onOk={excluir}
        confirmLoading={excluindo}
        okText="Excluir"
        okButtonProps={{ danger: true }}
        cancelText="Cancelar"
      >
        <Typography.Paragraph>
          Todos os arquivos transcodificados de <strong>{paraExcluir?.titulo}</strong> serão
          apagados do servidor. Essa ação é irreversível.
        </Typography.Paragraph>
      </Modal>

      {erroCarga && <Alert type="error" showIcon message={erroCarga} style={{ margin: 10 }} />}

      {carregou ? (
        <MaterialReactTable
          columns={columns}
          data={backups}
          defaultColumn={{ minSize: 5, maxSize: 220, size: 120 }}
          enableColumnFilterModes
          enableColumnSorting
          enableRowActions
          enableFacetedValues
          columnFilterModeOptions={null}
          initialState={{
            showColumnFilters: true,
            pagination: { pageIndex: 0, pageSize: 10 },
          }}
          localization={MRT_Localization_PT_BR}
          positionActionsColumn="last"
          displayColumnDefOptions={{ "mrt-row-actions": { header: "", size: 130 } }}
          renderEmptyRowsFallback={() => (
            <Content
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: 40,
                gap: 6,
              }}
            >
              <AiOutlineSearch size={48} color="#888" />
              <h3>Nenhum backup de vídeo ainda.</h3>
              <p>Envie um arquivo para gerar o fallback próprio da CDN.</p>
            </Content>
          )}
          renderRowActions={({ row }) => (
            <Content style={{ display: "flex", gap: 8 }}>
              <Tooltip title="Assistir">
                <Button
                  icon={<FaPlay />}
                  disabled={row.original.status !== "concluido"}
                  onClick={() => setPreview(row.original)}
                />
              </Tooltip>
              <Tooltip title="Copiar código (vid_end)">
                <Button
                  icon={<FaCopy />}
                  disabled={row.original.status !== "concluido"}
                  onClick={() => copiarCodigo(row.original.id)}
                />
              </Tooltip>
              <Tooltip title="Excluir">
                <Button icon={<AiFillDelete />} danger onClick={() => setParaExcluir(row.original)} />
              </Tooltip>
            </Content>
          )}
          renderTopToolbarCustomActions={() => (
            <Content
              style={{ display: "flex", alignItems: "center", gap: 16, padding: 10, flexWrap: "wrap" }}
            >
              <Typography.Title level={4} style={{ margin: 0 }}>
                Vídeos Backup
              </Typography.Title>
              <Divider type="vertical" />
              <Button
                type="primary"
                icon={<FaCloudUploadAlt />}
                size="large"
                onClick={() => setModalUploadAberto(true)}
              >
                Enviar vídeo para backup
              </Button>
              <Button icon={<FaSync />} size="large" onClick={carregar}>
                Atualizar
              </Button>
              {(fila.processando || fila.naFila > 0) && (
                <Tag color="processing">
                  {fila.processando ? "Transcodificando" : "Ocioso"}
                  {fila.naFila > 0 ? ` · ${fila.naFila} na fila` : ""}
                </Tag>
              )}
            </Content>
          )}
          muiTableHeadCellFilterTextFieldProps={{ placeholder: "Filtrar" }}
        />
      ) : (
        <Aguarde />
      )}
    </>
  );
};

export default VideosBackup;
